import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { questionsApi } from '../lib/questions';
import { resultsApi } from '../lib/results';
import { api } from '../lib/api';
import {
  ArrowLeft,
  Check,
  X,
  Clock,
  Trophy,
  Home,
  ChevronRight,
  AlertTriangle,
  Heart,
  Lock
} from 'lucide-react';

const Simulation = () => {
  const navigate = useNavigate();
  const { user, subscription, loading: authLoading } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizFinished, setQuizFinished] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60 * 60); // 60 minut v sekundách
  const [timeExpired, setTimeExpired] = useState(false);
  const [resultSaved, setResultSaved] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [showPaywall, setShowPaywall] = useState(false);

  const hasActiveSubscription = subscription?.hasSubscription;

  // Spočítat výsledky
  const calculateResults = useCallback(() => {
    let correctCount = 0;
    let totalScore = 0;
    let maxScore = 0;

    questions.forEach((q, index) => {
      const points = q.scorevalue || q.score_value || 1;
      maxScore += points;
      if (answers[index]?.isCorrect) {
        correctCount++;
        totalScore += points;
      }
    });

    return {
      correctCount,
      totalQuestions: questions.length,
      totalScore,
      maxScore,
      percentage: questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0
    };
  }, [questions, answers]);

  // Ukončit test
  const finishTest = useCallback(() => {
    setQuizFinished(true);
  }, []);

  // Uložit výsledek do databáze
  const saveSimulationResult = useCallback(async () => {
    if (resultSaved || !user || questions.length === 0) return;

    try {
      const results = calculateResults();
      await resultsApi.saveResult(user.id, {
        type: 'simulation',
        category: null,
        score: results.correctCount,
        totalQuestions: results.totalQuestions,
        percentage: results.percentage,
        timeSeconds: 3600 - timeRemaining, // Skutečný strávený čas
        answers: answers.map((a, idx) => ({
          questionId: a?.questionId,
          questionText: questions[idx]?.question_text,
          options: questions[idx]?.options,
          imageName: questions[idx]?.image_name,
          scoreValue: questions[idx]?.scorevalue || questions[idx]?.score_value || 1,
          selected: a?.selected,
          correct: a?.correct,
          isCorrect: a?.isCorrect
        })),
        totalScore: results.totalScore,
        maxScore: results.maxScore
      });
      setResultSaved(true);
      console.log('Simulation result saved successfully');
    } catch (error) {
      console.error('Error saving simulation result:', error);
    }
  }, [resultSaved, user, questions, answers, timeRemaining, calculateResults]);

  // Načíst oblíbené při startu
  const loadFavorites = async () => {
    if (!user) return;
    try {
      const ids = await resultsApi.getFavoriteIds(user.id);
      setFavoriteIds(ids);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  // Toggle oblíbené
  const toggleFavorite = async (questionId) => {
    if (!user) return;

    try {
      if (favoriteIds.has(questionId)) {
        await resultsApi.removeFavorite(user.id, questionId);
        setFavoriteIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(questionId);
          return newSet;
        });
      } else {
        await resultsApi.addFavorite(user.id, questionId);
        setFavoriteIds(prev => new Set([...prev, questionId]));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Přesměrovat nepřihlášeného uživatele
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { redirectTo: '/simulation' } });
    }
  }, [authLoading, user, navigate]);

  // Pro přihlášeného bez předplatného zobrazit paywall
  useEffect(() => {
    if (!authLoading && user && !hasActiveSubscription) {
      setShowPaywall(true);
    }
  }, [authLoading, user, hasActiveSubscription]);

  useEffect(() => {
    if (user && hasActiveSubscription) {
      loadQuestions();
      loadFavorites();
    }
  }, [user, hasActiveSubscription]);

  // Uložit výsledek když je simulace dokončena
  useEffect(() => {
    if (quizFinished && !resultSaved && user) {
      saveSimulationResult();
    }
  }, [quizFinished, resultSaved, user, saveSimulationResult]);

  // Časovač - odpočítávání
  useEffect(() => {
    if (quizFinished || loading || timeExpired) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setTimeExpired(true);
          finishTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizFinished, loading, timeExpired, finishTest]);

  const loadQuestions = async () => {
    try {
      setLoading(true);

      // Načíst náhodné otázky z každé kategorie
      const [fyzikaQuestions, chemieQuestions, biologieQuestions] = await Promise.all([
        questionsApi.getRandomQuestions('Fyzika', 20),
        questionsApi.getRandomQuestions('Chemie', 20),
        questionsApi.getRandomQuestions('Biologie', 20)
      ]);

      // Spojit v pořadí: Fyzika, Chemie, Biologie
      const allQuestions = [
        ...fyzikaQuestions,
        ...chemieQuestions,
        ...biologieQuestions
      ];

      setQuestions(allQuestions);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (selectedAnswer !== null || quizFinished) return;

    setSelectedAnswer(index);
    const isCorrect = index === questions[currentIndex].correct_answer_index;

    setAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[currentIndex] = {
        questionId: questions[currentIndex].id,
        selected: index,
        correct: questions[currentIndex].correct_answer_index,
        isCorrect,
        scoreValue: questions[currentIndex].scorevalue || questions[currentIndex].score_value || 1
      };
      return newAnswers;
    });

    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(answers[currentIndex + 1]?.selected ?? null);
      setShowExplanation(answers[currentIndex + 1] !== undefined);
    } else {
      finishTest();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setSelectedAnswer(answers[currentIndex - 1]?.selected ?? null);
      setShowExplanation(answers[currentIndex - 1] !== undefined);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCategoryForIndex = (index) => {
    if (index < 20) return 'Fyzika';
    if (index < 40) return 'Chemie';
    return 'Biologie';
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Fyzika': return 'bg-pink-500/20 text-pink-400';
      case 'Chemie': return 'bg-blue-500/20 text-blue-400';
      case 'Biologie': return 'bg-green-500/20 text-green-400';
      default: return 'bg-primary-500/20 text-primary-400';
    }
  };

  // Zobrazit paywall pro uživatele bez předplatného
  if (showPaywall) {
    const plans = [
      { key: '1_month', name: '1 měsíc', price: 249, pricePerMonth: 249 },
      { key: '3_months', name: '3 měsíce', price: 699, pricePerMonth: 233 },
      { key: '6_months', name: '6 měsíců', price: 1290, pricePerMonth: 215, popular: true },
    ];

    const handleSubscribe = async (planKey) => {
      try {
        const response = await api.createCheckoutSession(planKey, user.id, user.email);
        if (response.url) {
          window.location.href = response.url;
        } else if (response.error) {
          alert('Chyba: ' + response.error);
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Nepodařilo se vytvořit platbu. Zkuste to znovu.');
      }
    };

    return (
      <div className="min-h-screen bg-dark-950 pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-6">
              <Lock size={40} className="text-white" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">Simulace přijímaček</h1>
            <p className="text-dark-400 mb-6">
              Simulace vyžaduje aktivní předplatné
            </p>

            <div className="glass rounded-xl p-6 mb-6 text-left">
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Check size={18} className="text-green-400" />
                  <span className="text-dark-300">Časový limit 60 minut</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check size={18} className="text-green-400" />
                  <span className="text-dark-300">20 otázek z Fyziky, 20 z Chemie, 20 z Biologie</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check size={18} className="text-green-400" />
                  <span className="text-dark-300">Vyhodnocení s body jako u zkoušky</span>
                </li>
              </ul>
            </div>

            {/* Pricing buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {plans.map((plan) => (
                <button
                  key={plan.key}
                  onClick={() => handleSubscribe(plan.key)}
                  className={`relative rounded-xl p-4 transition-all hover:scale-105 ${
                    plan.popular
                      ? 'bg-gradient-to-b from-primary-900/50 to-dark-800 border-2 border-primary-500'
                      : 'glass border border-dark-700 hover:border-dark-600'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="px-2 py-0.5 rounded-full gradient-bg text-white text-xs font-semibold">
                        Nejlepší
                      </div>
                    </div>
                  )}
                  <div className="text-center">
                    <h4 className="text-white font-semibold">{plan.name}</h4>
                    <div className="flex items-baseline justify-center gap-1 mt-1">
                      <span className="text-2xl font-bold text-white">{plan.price}</span>
                      <span className="text-dark-400 text-sm">Kč</span>
                    </div>
                    <p className="text-dark-500 text-xs mt-1">
                      {plan.pricePerMonth} Kč/měsíc
                    </p>
                    <div className={`mt-3 py-1.5 rounded-lg text-sm font-semibold ${
                      plan.popular
                        ? 'gradient-bg text-white'
                        : 'bg-dark-700 text-white'
                    }`}>
                      Vybrat
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="text-dark-400 hover:text-white transition-colors"
            >
              Zpět na Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <div className="text-white">Připravuji simulaci přijímaček...</div>
          <div className="text-dark-400 text-sm mt-2">Načítám 60 otázek</div>
        </div>
      </div>
    );
  }

  if (quizFinished) {
    const results = calculateResults();

    return (
      <div className="min-h-screen bg-dark-950 pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-6">
              <Trophy size={40} className="text-white" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">
              {timeExpired ? 'Čas vypršel!' : 'Simulace dokončena!'}
            </h1>
            <p className="text-dark-400 mb-8">Simulace přijímacích zkoušek</p>

            {timeExpired && (
              <div className="mb-6 p-4 rounded-xl bg-amber-500/20 border border-amber-500/30">
                <div className="flex items-center justify-center space-x-2 text-amber-400">
                  <AlertTriangle size={20} />
                  <span>Časový limit 60 minut vypršel</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="glass rounded-xl p-4">
                <p className="text-3xl font-bold text-primary-400">{results.correctCount}</p>
                <p className="text-dark-400 text-sm">Správně</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{results.totalQuestions}</p>
                <p className="text-dark-400 text-sm">Celkem otázek</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-3xl font-bold text-green-400">{results.totalScore}/{results.maxScore}</p>
                <p className="text-dark-400 text-sm">Body</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{results.percentage}%</p>
                <p className="text-dark-400 text-sm">Úspěšnost</p>
              </div>
            </div>

            {/* Výsledky podle kategorií */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Výsledky podle předmětů</h3>
              <div className="grid grid-cols-3 gap-4">
                {['Fyzika', 'Chemie', 'Biologie'].map((cat, catIndex) => {
                  const startIdx = catIndex * 20;
                  const catAnswers = answers.slice(startIdx, startIdx + 20);
                  const catCorrect = catAnswers.filter(a => a?.isCorrect).length;
                  return (
                    <div key={cat} className="glass rounded-xl p-4">
                      <p className={`text-sm font-medium mb-1 ${getCategoryColor(cat).split(' ')[1]}`}>{cat}</p>
                      <p className="text-2xl font-bold text-white">{catCorrect}/20</p>
                      <p className="text-dark-400 text-xs">{Math.round((catCorrect / 20) * 100)}%</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors flex items-center justify-center space-x-2"
              >
                <span>Nová simulace</span>
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 rounded-xl bg-dark-700 text-white font-semibold hover:bg-dark-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Home size={20} />
                <span>Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const currentCategory = getCategoryForIndex(currentIndex);
  const isLowTime = timeRemaining < 300; // Méně než 5 minut

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-dark-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Ukončit</span>
          </button>

          <div className="flex items-center space-x-4">
            {/* Časovač */}
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${isLowTime ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-dark-800 text-white'}`}>
              <Clock size={18} />
              <span className="font-mono font-bold text-lg">{formatTime(timeRemaining)}</span>
            </div>

            {/* Číslo otázky */}
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max={questions.length}
                value={currentIndex + 1}
                onChange={(e) => {
                  const num = parseInt(e.target.value);
                  if (num >= 1 && num <= questions.length) {
                    const newIndex = num - 1;
                    setCurrentIndex(newIndex);
                    setSelectedAnswer(answers[newIndex]?.selected ?? null);
                    setShowExplanation(answers[newIndex] !== undefined);
                  }
                }}
                className="w-16 px-2 py-1 rounded-lg bg-dark-800 text-white text-center border border-dark-700 focus:border-primary-500 focus:outline-none"
              />
              <span className="text-white font-medium">/ {questions.length}</span>
            </div>
          </div>
        </div>

        {/* Sekce indikátor */}
        <div className="flex justify-center gap-2 mb-4">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${currentIndex < 20 ? 'bg-pink-500 text-white' : 'bg-dark-700 text-dark-400'}`}>
            Fyzika (1-20)
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${currentIndex >= 20 && currentIndex < 40 ? 'bg-blue-500 text-white' : 'bg-dark-700 text-dark-400'}`}>
            Chemie (21-40)
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${currentIndex >= 40 ? 'bg-green-500 text-white' : 'bg-dark-700 text-dark-400'}`}>
            Biologie (41-60)
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <input
            type="range"
            min="0"
            max={questions.length - 1}
            value={currentIndex}
            onChange={(e) => {
              const newIndex = parseInt(e.target.value);
              setCurrentIndex(newIndex);
              setSelectedAnswer(answers[newIndex]?.selected ?? null);
              setShowExplanation(answers[newIndex] !== undefined);
            }}
            className="w-full h-2 bg-dark-800 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-4
              [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-primary-500
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-moz-range-thumb]:w-4
              [&::-moz-range-thumb]:h-4
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-primary-500
              [&::-moz-range-thumb]:cursor-pointer
              [&::-moz-range-thumb]:border-0"
            style={{
              background: `linear-gradient(to right, rgb(139, 92, 246) 0%, rgb(168, 85, 247) ${((currentIndex + 1) / questions.length) * 100}%, rgb(31, 41, 55) ${((currentIndex + 1) / questions.length) * 100}%, rgb(31, 41, 55) 100%)`
            }}
          />
        </div>

        {/* Question */}
        <div className="glass rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(currentCategory)}`}>
                {currentCategory}
              </span>
              <span className="px-3 py-1 rounded-full bg-dark-700 text-dark-300 text-sm font-medium">
                Otázka {currentIndex + 1}
              </span>
              <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium">
                {currentQuestion.scorevalue || currentQuestion.score_value || 1} {(currentQuestion.scorevalue || currentQuestion.score_value || 1) === 1 ? 'bod' : 'body'}
              </span>
            </div>
            {/* Tlačítko oblíbené */}
            <button
              onClick={() => toggleFavorite(currentQuestion.id)}
              className={`p-2 rounded-lg transition-colors ${
                favoriteIds.has(currentQuestion.id)
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-dark-700 text-dark-400 hover:text-red-400'
              }`}
              title={favoriteIds.has(currentQuestion.id) ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
            >
              <Heart
                size={20}
                fill={favoriteIds.has(currentQuestion.id) ? 'currentColor' : 'none'}
              />
            </button>
          </div>

          <h2 className="text-xl font-semibold text-white mb-6 whitespace-pre-line">
            {currentQuestion.question_text}
          </h2>

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === currentQuestion.correct_answer_index;
              const showResult = selectedAnswer !== null;

              let buttonClass = 'glass';
              if (showResult) {
                if (isCorrect) {
                  buttonClass = 'bg-green-500/20 border border-green-500';
                } else if (isSelected && !isCorrect) {
                  buttonClass = 'bg-red-500/20 border border-red-500';
                }
              } else if (isSelected) {
                buttonClass = 'bg-primary-500/20 border border-primary-500';
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={selectedAnswer !== null}
                  className={`w-full p-4 rounded-xl text-left transition-all ${buttonClass} ${
                    selectedAnswer === null ? 'hover:bg-dark-700' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white">{option}</span>
                    {showResult && isCorrect && (
                      <Check size={20} className="text-green-400" />
                    )}
                    {showResult && isSelected && !isCorrect && (
                      <X size={20} className="text-red-400" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Explanation */}
        {showExplanation && currentQuestion.explanation && (
          <div className="glass rounded-2xl p-6 mb-6 border border-primary-500/30">
            <h3 className="text-lg font-semibold text-white mb-2">Vysvětlení</h3>
            <p className="text-dark-300">{currentQuestion.explanation}</p>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`px-6 py-3 rounded-xl font-semibold transition-colors flex items-center space-x-2 ${
              currentIndex === 0
                ? 'bg-dark-800 text-dark-500 cursor-not-allowed'
                : 'bg-dark-700 text-white hover:bg-dark-600'
            }`}
          >
            <ArrowLeft size={20} />
            <span>Předchozí</span>
          </button>

          <button
            onClick={finishTest}
            className="px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors"
          >
            Odevzdat test
          </button>

          <button
            onClick={handleNext}
            className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors flex items-center space-x-2"
          >
            <span>{currentIndex < questions.length - 1 ? 'Další' : 'Dokončit'}</span>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Simulation;
